import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import type { Subscription } from 'rxjs';

import { isClientAccount, type User } from '../../../../shared/models/user.model';
import { AuthService } from '../../../../shared/services/auth.service';
import { userAvatarUrl } from '../../../../shared/utils/user-avatar.util';
import {
  type AccountMailNotice,
  AccountMailCooldownStore,
  accountMailNotice,
} from '../../data/account-mail-cooldown.store';
import { PROFILE_COPY } from '../../profile.copy';
import { formatClientSince } from '../../utils/profile-format.util';
import { MailCooldownButtonComponent } from '../mail-cooldown-button/mail-cooldown-button.component';

/** Mascota que sustituye al avatar cuando no hay o no carga. */
const MASCOT_AVATAR = 'images/logo/comunity.png';

/**
 * Cabecera de `/perfil` (W0b, §5.6.2 de B10): solo datos reales de la cuenta (`/identify-user`). Avatar (o la
 * mascota), nombre como `h1`, correo, «Cliente desde…» y, para clientes sin verificar, el aviso B24 con el botón
 * «Reenviar correo de verificación» (W3), que comparte la espera con el de ajustes.
 */
@Component({
  selector: 'app-profile-header',
  imports: [MailCooldownButtonComponent],
  templateUrl: './profile-header.component.html',
  styleUrl: './profile-header.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class ProfileHeaderComponent {
  private readonly auth = inject(AuthService);
  private readonly mail = inject(AccountMailCooldownStore);

  readonly user = input.required<User>();

  protected readonly copy = PROFILE_COPY.HEADER;
  protected readonly settingsCopy = PROFILE_COPY.SETTINGS;
  protected readonly mascotAvatar = MASCOT_AVATAR;

  /** URL que falló al cargar: si el usuario cambia de avatar, la nueva se vuelve a intentar. */
  private readonly failedAvatar = signal<string | null>(null);

  protected readonly avatarUrl = computed(() => {
    const url = userAvatarUrl(this.user().avatar);
    return url && url !== this.failedAvatar() ? url : null;
  });

  protected readonly fullName = computed(() =>
    [this.user().name, this.user().surnames]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' '),
  );

  protected readonly clientSince = computed(() => formatClientSince(this.user().created_at));

  /** B24: solo cuentas `cliente` y solo con `false` explícito (`undefined` no es «sin verificar»). */
  protected readonly showUnverified = computed(
    () => isClientAccount(this.user()) && this.user().verified === false,
  );

  /** Aviso del reenvío, con la cuenta que lo pidió: con otra cuenta no se pinta (ni antes de que corra el efecto). */
  private readonly resendResult = signal<{ owner: number; notice: AccountMailNotice } | null>(null);
  private resendSub: Subscription | null = null;

  protected readonly resendNotice = computed(() => {
    const result = this.resendResult();
    return result && result.owner === this.user().id ? result.notice : null;
  });

  private readonly userId = computed(() => this.user().id);

  constructor() {
    // La cabecera sigue montada si se pasa de A a B sin cerrar sesión: el aviso de A se va con A.
    effect(() => {
      this.userId();
      untracked(() => this.forgetResend());
    });
    inject(DestroyRef).onDestroy(() => this.resendSub?.unsubscribe());
  }

  protected onAvatarError(): void {
    this.failedAvatar.set(this.avatarUrl());
  }

  protected resend(): void {
    const owner = this.user().id;
    this.resendSub?.unsubscribe();
    this.resendSub = this.mail.send('resend', this.auth.resendVerificationEmail(owner)).subscribe((outcome) => {
      this.resendResult.set({ owner, notice: accountMailNotice(outcome, this.settingsCopy.RESEND_SENT) });
    });
  }

  private forgetResend(): void {
    this.resendSub?.unsubscribe();
    this.resendSub = null;
    this.resendResult.set(null);
  }
}
